import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Box, Button, Container, Link, Typography } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { APP_PATH_AI, APP_PATH_PRODUCTS } from '../../config';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: theme.palette.background.default
  },
  heroSection: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    textAlign: 'left'
  },
  heroTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  heroSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem'
    }
  },
  heroDescription: {
    fontSize: '1.125rem',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  section: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6)
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(4),
    textAlign: 'left',
    color: theme.palette.text.primary
  },
  list: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  listItem: {
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
    lineHeight: 1.7
  },
  valuesList: {
    listStyle: 'none',
    padding: 0,
    marginTop: theme.spacing(3)
  },
  valueItem: {
    marginBottom: theme.spacing(3)
  },
  valueTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  valueDescription: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    lineHeight: 1.7
  },
  inlineLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  ctaSection: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    textAlign: 'left',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(6)
  },
  ctaTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  ctaDescription: {
    fontSize: '1.125rem',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  textLink: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}));

const About = () => {
  const classes = useStyles();
  usePageTitle('About Remote Job');

  return (
    <div className={classes.root}>
      <Helmet>
        <title>About Remote Job | Hiring platform for remote-first teams</title>
      </Helmet>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <Typography variant="h2" className={classes.heroTitle}>
            About Remote Job
          </Typography>
          <Typography variant="h4" className={classes.heroSubtitle}>
            A better way to hire remotely — built for how teams actually work
            today.
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote work is no longer an experiment.
          </Typography>
          <Typography className={classes.heroDescription}>
            It’s how modern teams operate.
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote Job was created to make remote hiring simple, structured, and
            human — without forcing companies to juggle tools, spreadsheets, or
            disconnected workflows.
          </Typography>
          <Typography className={classes.heroDescription}>
            We believe hiring should be clear, efficient, and fair — whether
            your team is across the street or across the world.
          </Typography>
        </Box>

        {/* Mission Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            Our Mission
          </Typography>
          <Typography className={classes.heroDescription}>
            To help companies and people do their best work — from anywhere.
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote Job exists to remove friction from remote hiring.
          </Typography>
          <Typography className={classes.heroDescription}>
            We connect companies with global talent through a complete hiring
            workspace, not just a job listing. From the moment a job is posted
            to the final hiring decision, everything happens in one place.
          </Typography>
          <Typography className={classes.heroDescription}>
            No unnecessary complexity.
            <br />
            No hidden processes.
            <br />
            No barriers based on location.
          </Typography>
        </Box>

        {/* What We're Building Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            What We’re Building
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote Job is more than a job board.
          </Typography>
          <Typography className={classes.heroDescription}>
            It’s a platform where companies can:
          </Typography>
          <Box component="ul" className={classes.list}>
            <li className={classes.listItem}>Post remote-friendly jobs</li>
            <li className={classes.listItem}>
              Manage candidates through a clear hiring pipeline
            </li>
            <li className={classes.listItem}>
              Track applications and activity in one dashboard
            </li>
            <li className={classes.listItem}>
              Communicate directly with candidates
            </li>
            <li className={classes.listItem}>
              Schedule interviews and collect structured feedback
            </li>
            <li className={classes.listItem}>
              Control job visibility by region when needed
            </li>
          </Box>
          <Typography className={classes.heroDescription}>
            And we’re continuing to evolve.
          </Typography>
          <Typography className={classes.heroDescription}>
            We’re building practical AI-powered tools to help hiring teams:
          </Typography>
          <Box component="ul" className={classes.list}>
            <li className={classes.listItem}>Review candidates faster</li>
            <li className={classes.listItem}>Get clearer fit insights</li>
            <li className={classes.listItem}>
              Reduce manual work — without replacing human decision-making
            </li>
          </Box>
          <Typography className={classes.heroDescription}>
            These tools are designed to support hiring decisions, not automate
            them.{' '}
            <Link
              component={RouterLink}
              to={APP_PATH_AI}
              className={classes.textLink}>
              Learn what’s coming
            </Link>
            .
          </Typography>
        </Box>

        {/* Values Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            What We Stand For
          </Typography>
          <Box className={classes.valuesList}>
            <Box className={classes.valueItem}>
              <Typography className={classes.valueDescription}>
                <span className={classes.inlineLabel}>
                  Trust and Transparency —{' '}
                </span>
                Clear information, honest processes, and direct communication
                between companies and candidates.
              </Typography>
            </Box>

            <Box className={classes.valueItem}>
              <Typography className={classes.valueDescription}>
                <span className={classes.inlineLabel}>
                  Quality Over Noise —{' '}
                </span>
                Fewer tools. Better workflows. Hiring that focuses on real fit,
                not volume.
              </Typography>
            </Box>

            <Box className={classes.valueItem}>
              <Typography className={classes.valueDescription}>
                <span className={classes.inlineLabel}>
                  Human-Centered Hiring —{' '}
                </span>
                Behind every application is a person. Behind every job is a
                team. We build with both in mind.
              </Typography>
            </Box>

            <Box className={classes.valueItem}>
              <Typography className={classes.valueDescription}>
                <span className={classes.inlineLabel}>
                  Global Opportunity —{' '}
                </span>
                Skills matter more than location. Great teams can be built
                anywhere.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Who Uses Remote Job Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            Who Uses Remote Job
          </Typography>
          <Box className={classes.valuesList}>
            <Box className={classes.valueItem}>
              <Typography className={classes.valueTitle}>
                Companies & Hiring Teams
              </Typography>
              <Typography className={classes.valueDescription}>
                From startups to established organizations, Remote Job helps
                teams hire globally without losing structure or speed.
              </Typography>
            </Box>

            <Box className={classes.valueItem}>
              <Typography className={classes.valueTitle}>
                Remote Professionals
              </Typography>
              <Typography className={classes.valueDescription}>
                Candidates use Remote Job to find meaningful remote roles, apply
                with confidence, and track their progress — all in one place.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Why We Exist Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            Why Remote Job Exists
          </Typography>
          <Typography className={classes.heroDescription}>
            We saw companies struggling to manage remote hiring across
            disconnected tools. We saw talented people limited by geography
            instead of skill.
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote Job was built to bring clarity to both sides — creating a
            hiring experience that feels organized, fair, and human.
          </Typography>
        </Box>

        {/* Looking Ahead Section */}
        <Box className={classes.section}>
          <Typography variant="h3" className={classes.sectionTitle} component="h2">
            Looking Ahead
          </Typography>
          <Typography className={classes.heroDescription}>
            Remote Job is growing alongside the teams that use it.
          </Typography>
          <Typography className={classes.heroDescription}>
            We’re focused on:
          </Typography>
          <Box component="ul" className={classes.list}>
            <li className={classes.listItem}>Improving hiring workflows</li>
            <li className={classes.listItem}>Reducing friction for recruiters</li>
            <li className={classes.listItem}>
              Giving candidates more clarity and confidence
            </li>
            <li className={classes.listItem}>
              Introducing AI responsibly — where it adds real value
            </li>
          </Box>
          <Typography className={classes.heroDescription}>
            Always with the same goal: helping teams hire better, wherever they
            are.
          </Typography>
        </Box>

        {/* CTA Section */}
        <Box className={classes.ctaSection}>
          <Typography variant="h3" className={classes.ctaTitle} component="h2">
            Join Us
          </Typography>
          <Typography className={classes.ctaDescription}>
            Whether you’re here to hire, explore, or understand how modern
            remote teams work — you’re welcome.
          </Typography>
          <Typography className={classes.ctaDescription}>
            Remote Job is helping shape the future of hiring.
            <br />
            One role, one team, one connection at a time.
          </Typography>
          <RouterLink
            to={APP_PATH_PRODUCTS}
            style={{ textDecoration: 'none' }}>
            <Button variant="contained" color="primary" size="large">
              Explore Platform Features
            </Button>
          </RouterLink>
        </Box>
      </Container>
    </div>
  );
};

export default About;
